import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Logger from '../../../common/logger';
import {
    invalidateInventoryReaders,
    useDivideRoomMutation,
} from '../../../api/inventory/inventoryApiSlice';
import { readInventoryFailure } from '../../../api/inventory/inventoryErrors';
import type { DivideRoomResponse } from '../../../api/station/stationApiTypes';
import CurrencyAmountInput from '../../../components/CurrencyAmountInput';
import NetGrossField from '../../../components/pricing/NetGrossField';
import { useTaxRateOf } from '../../../hooks/usePriceEntry';
import CustomButton from '../../../components/CustomButton';
import LabelWithHelp from '../../../components/LabelWithHelp';
import CustomModal from '../../common/CustomModal';
import {
    MAX_SEGMENTS,
    MIN_SEGMENTS,
    SegmentDraft,
    refusalKeyFor,
    suggestWholeCapacity,
    toSegmentPayload,
    validateSegments,
    wholeCapacityProblem,
} from '../divideRoomRules';

const fieldClass =
    'w-full border border-foreground/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary';

const emptyDraft = (description: string): SegmentDraft => ({
    name: '',
    price: '',
    priceBadInput: false,
    // Required, and deliberately NOT prefilled with anything: no number the
    // room carries is a defensible starting point for a part of it, and a
    // prefilled one would be accepted unread.
    capacity: '',
    capacityBadInput: false,
    // Prefilled, because this one IS safe to carry over — it describes the
    // physical space, which a part of that space shares.
    description,
});

interface Props {
    /** The Product behind the room being divided. */
    productId: string;
    /** For the dialog's subtitle — the operator should see which room they are
     *  about to change, since the list can be long. */
    roomName: string | null;
    /** The room's description, prefilled into every part and editable there. */
    roomDescription: string;
    /** The room's image, carried onto every part. Shown, not editable — see
     *  the header. `null` renders the "no image" note rather than a broken box. */
    roomImage: string | null;
    /** The whole room's stored guest capacity, shown next to the suggestion so
     *  it is never overwritten unseen. `null` = none stored. */
    roomCapacity: number | null;
    /** The room's tax category, which every part inherits — net entry converts
     *  at its rate (#2967). `null` = none set. */
    taxCategoryId: string | null;
    onClose: () => void;
}

/**
 * **Divide a room into segments** — the authoring half of #1714, and D13's
 * "the operator says *this room can be divided into 2 or 3 parts* and names
 * them".
 *
 * The words `exclusive`, `product_resource_link` and `Group` never appear.
 * Two Products over shared objects is a modelling concept, not a screen
 * (ADR-0003, curated over generic): what the operator says is how many parts
 * there are, what they are called and what each costs, and the endpoint
 * generates the Products, cells, objects and links behind it.
 *
 * ── Why the refusals arrive late ───────────────────────────────────────────
 *
 * Whether a room CAN be divided depends on live rows — how many objects fulfil
 * it, whether it is already divided, whether anything is booked in the future
 * (D12). The browser does not re-derive any of that: `assessDivisibility` on
 * the backend is the single answer, and a second one here could disagree with
 * it. So the action is offered on every room and a refusal comes back as a 409
 * with a `reason`, rendered as its own sentence with its own remedy.
 *
 * ── After a successful division ────────────────────────────────────────────
 *
 * The dialog does NOT close. It switches to a result panel, because a segment
 * created without a price is offered to customers as "price on request" rather
 * than as a bookable room (measured, #2630 C2) — and that is the one thing the
 * operator must not miss. Closing on success would report the write as finished
 * while leaving half of it enquiry-only, and such a segment looks exactly like a
 * priced one in the list behind.
 *
 * ── What a part inherits, and what it cannot ───────────────────────────────
 *
 * The PO's rule (2026-09-19): *inherit what is safe, leave blank what would be
 * an invention, and show every one of them here so the operator reviews before
 * saving.*
 *
 * So the room's image and description are carried over and shown — the image
 * read-only (replacing one is the segment's own product screen, which already
 * has the upload), the description editable. **Guest capacity is neither**: it
 * is typed per part, and the dialog will not save without it. Halving the
 * room's number is at most a starting point and the PO does not want it offered
 * as one, because real capacity depends on layout, furniture and fire
 * regulations that the room's own figure does not contain.
 *
 * The deposit is deliberately not carried over at all and is not shown: it
 * belongs to the physical room and is held once for any booking touching it, so
 * three parts must not collect three deposits (#2230 §4).
 */
const DivideRoomDialog = ({
    productId, roomName, roomDescription, roomImage, roomCapacity, taxCategoryId, onClose,
}: Props) => {
    const { t } = useTranslation();
    const rate = useTaxRateOf([taxCategoryId]);
    const dispatch = useDispatch();
    const [drafts, setDrafts] = useState<SegmentDraft[]>(
        () => [emptyDraft(roomDescription), emptyDraft(roomDescription)],
    );
    const [formError, setFormError] = useState<string | null>(null);
    const [result, setResult] = useState<DivideRoomResponse | null>(null);
    const [divideRoom, { isLoading }] = useDivideRoomMutation();

    const problem = useMemo(() => validateSegments(drafts), [drafts]);
    // The whole room's capacity: the sum of the parts is SUGGESTED until the
    // operator types their own value (`null` = not touched yet).
    const [wholeCapacityInput, setWholeCapacityInput] = useState<string | null>(null);
    const wholeCapacity = wholeCapacityInput ?? suggestWholeCapacity(drafts);
    const wholeProblem = wholeCapacityProblem(wholeCapacity, drafts);
    const isValid = problem === null && wholeProblem === null;

    const patchDraft = (index: number, patch: Partial<SegmentDraft>) => {
        setDrafts((current) => current.map(
            (draft, i) => (i === index ? { ...draft, ...patch } : draft),
        ));
        setFormError(null);
    };

    const handleSubmit = async () => {
        if (!isValid || isLoading) return;
        setFormError(null);
        try {
            const response = await divideRoom({
                productId,
                segments: toSegmentPayload(drafts),
                ...(wholeCapacity.trim() === '' ? {} : { wholeRoomCapacity: Number(wholeCapacity.trim()) }),
            }).unwrap();
            // The new Products, cells and objects are read by five slices,
            // which tags cannot reach across. It also invalidates
            // `PricingStructure`, which IS this dialog's own caller — that
            // query is subscribed, so the room list behind refreshes itself
            // and needs no callback.
            invalidateInventoryReaders(dispatch);
            setResult(response);
        } catch (err) {
            const failure = readInventoryFailure(err);
            const refusalKey = refusalKeyFor(failure.reason);
            if (refusalKey) {
                setFormError(t(refusalKey));
            } else if (failure.unreachable) {
                // Distinguished from a rejection on purpose: "no response" and
                // "refused" have different remedies (#2255).
                setFormError(t('services.divide_room.unreachable'));
            } else {
                // The server's own message behind a failure, when it sent one —
                // never swallowed into a generic sentence.
                setFormError(failure.message ?? t('services.divide_room.save_error'));
            }
            Logger('DivideRoomDialog').error(`Divide failed: ${JSON.stringify(err)}`);
        }
    };

    if (result) {
        const unbookable = result.unbookableSegments;
        return (
            <CustomModal
                open={true}
                title={t('services.divide_room.done_title')}
                // `parts`, not `count`: i18next reads `count` as the plural
                // selector and would look for `_one` / `_other` first.
                description={t('services.divide_room.done_description', {
                    parts: result.segments.length,
                })}
                onClose={onClose}
                saveButton={<CustomButton title={t('common.close')} onClick={onClose} />}
            >
                <div className={'flex flex-col gap-4'}>
                    <ul className={'flex flex-col gap-1'}>
                        {result.segments.map((segment) => (
                            <li
                                key={segment.productId}
                                className={'flex items-center justify-between text-sm text-foreground'}
                            >
                                <span>{segment.name}</span>
                                <span className={'text-xs text-foreground/60'}>
                                    {t(segment.bookable
                                        ? 'services.divide_room.segment_bookable'
                                        : 'services.divide_room.segment_unbookable')}
                                </span>
                            </li>
                        ))}
                    </ul>

                    {/* Said out loud, not left to be noticed. A segment with no
                        price was created but cannot be booked, and nothing else
                        on screen distinguishes it from one that can. */}
                    {unbookable.length > 0 && (
                        <div className={'rounded-[5px] border border-foreground/15 bg-foreground/5'
                            + ' px-4 py-3 text-sm text-foreground/70'}
                        >
                            {t('services.divide_room.unbookable_notice', {
                                names: unbookable.join(', '),
                            })}
                        </div>
                    )}
                </div>
            </CustomModal>
        );
    }

    return (
        <CustomModal
            open={true}
            title={t('services.divide_room.title')}
            description={roomName ?? undefined}
            onClose={onClose}
            closeButton={
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={onClose} />
            }
            saveButton={
                <CustomButton
                    title={t('services.divide_room.submit')}
                    loading={isLoading}
                    disabled={!isValid || isLoading}
                    onClick={handleSubmit}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                {/* What the operator is actually agreeing to, in their own
                    words: the room stays bookable whole, and booking either
                    side takes the other out of the calendar. Without this the
                    feature reads as "make two more rooms". */}
                <p className={'text-sm text-foreground/70'}>
                    {t('services.divide_room.explainer')}
                </p>

                {drafts.map((draft, index) => (
                    // Index-keyed deliberately: rows are positional, have no id
                    // of their own until the server makes one, and are only
                    // ever appended or removed from the end.
                    <div
                        key={index}
                        className={'rounded-[5px] border border-foreground/10 p-4 flex flex-col gap-3'}
                    >
                        <div className={'flex items-center justify-between'}>
                            <p className={'text-sm font-semibold text-foreground'}>
                                {t('services.divide_room.segment_heading', { index: index + 1 })}
                            </p>
                            {drafts.length > MIN_SEGMENTS && index === drafts.length - 1 && (
                                <button
                                    type={'button'}
                                    className={'text-sm text-primary hover:underline'}
                                    onClick={() => {
                                        setDrafts((current) => current.slice(0, -1));
                                        setFormError(null);
                                    }}
                                >
                                    {t('services.divide_room.remove_segment')}
                                </button>
                            )}
                        </div>

                        <div className={'flex flex-col gap-1'}>
                            <label
                                className={'text-sm font-medium text-foreground'}
                                htmlFor={`segment-name-${index}`}
                            >
                                {t('services.divide_room.segment_name_label')}
                            </label>
                            <input
                                id={`segment-name-${index}`}
                                type={'text'}
                                className={fieldClass}
                                value={draft.name}
                                placeholder={t('services.divide_room.segment_name_placeholder')}
                                onChange={(e) => patchDraft(index, { name: e.target.value })}
                            />
                        </div>

                        <div className={'flex flex-col gap-1'}>
                            <label
                                className={'text-sm font-medium text-foreground'}
                                htmlFor={`segment-capacity-${index}`}
                            >
                                {t('services.divide_room.segment_capacity_label')}
                            </label>
                            <input
                                id={`segment-capacity-${index}`}
                                type={'number'}
                                min={1}
                                step={1}
                                className={fieldClass}
                                value={draft.capacity}
                                placeholder={
                                    t('services.divide_room.segment_capacity_placeholder')
                                }
                                onChange={(e) => patchDraft(index, {
                                    capacity: e.target.value,
                                    // Firefox reports junk as an EMPTY value
                                    // with this set — see SegmentDraft.
                                    capacityBadInput: e.target.validity.badInput,
                                })}
                            />
                            {/* The one field with no inherited value, so it says
                                why rather than leaving the operator to wonder
                                what the blank means. */}
                            <p className={'text-xs text-foreground/60'}>
                                {t('services.divide_room.segment_capacity_hint')}
                            </p>
                        </div>

                        <NetGrossField
                            gross={draft.price}
                            onGrossChange={(price) => patchDraft(index, { price })}
                            rate={rate}
                        >
                            {(input) => (
                                <CurrencyAmountInput
                                    id={`segment-price-${index}`}
                                    label={input.label(t('services.divide_room.segment_price_label'))}
                                    value={input.value}
                                    onValueChange={(value, badInput) => {
                                        // Junk input reads as '' with badInput
                                        // set (SegmentDraft); the flag is about
                                        // what was typed, so it is kept as is.
                                        patchDraft(index, { priceBadInput: badInput });
                                        input.onChange(value);
                                    }}
                                    hint={t('services.divide_room.segment_price_hint')}
                                />
                            )}
                        </NetGrossField>

                        <div className={'flex flex-col gap-1'}>
                            <label
                                className={'text-sm font-medium text-foreground'}
                                htmlFor={`segment-description-${index}`}
                            >
                                {t('services.divide_room.segment_description_label')}
                            </label>
                            <textarea
                                id={`segment-description-${index}`}
                                rows={2}
                                className={fieldClass}
                                value={draft.description}
                                onChange={(e) =>
                                    patchDraft(index, { description: e.target.value })}
                            />
                        </div>

                        {/* The image is inherited and shown, not editable here:
                            replacing one belongs on the part's own product
                            screen, which already has the upload. Shown at all
                            because the operator has to know it carries over —
                            and because before this, three imageless cards
                            appeared in the customer's list with no warning. */}
                        <div className={'flex items-center gap-3'}>
                            {roomImage ? (
                                <img
                                    src={roomImage}
                                    alt={''}
                                    className={'h-10 w-10 rounded-[5px] object-cover'}
                                />
                            ) : (
                                <span className={'text-xs text-foreground/40 italic'}>
                                    {t('common.not_applicable')}
                                </span>
                            )}
                            <span className={'text-xs text-foreground/60'}>
                                {t('services.divide_room.segment_image_note')}
                            </span>
                        </div>
                    </div>
                ))}

                {drafts.length < MAX_SEGMENTS && (
                    <div>
                        <button
                            type={'button'}
                            className={'text-sm text-primary hover:underline font-medium'}
                            onClick={() => {
                                setDrafts((current) => [...current, emptyDraft(roomDescription)]);
                                setFormError(null);
                            }}
                        >
                            {t('services.divide_room.add_segment')}
                        </button>
                    </div>
                )}

                {/* The concept the operator cannot see from the form: the room
                    is not replaced, it keeps its own object, and each part is a
                    NEW object that starts from the room's image and text.

                    This used to say the room's object BECAME the first part,
                    carrying its door mapping and images. #2718 reversed exactly
                    that, and the text was left behind — so the dialog was
                    promising the operator an inheritance that had stopped
                    happening, which is the bug this change fixes. */}
                <LabelWithHelp
                    className={'mb-0'}
                    text={t('services.divide_room.first_segment_label')}
                    help={t('services.divide_room.first_segment_help')}
                />

                <div className={'flex flex-col gap-1'}>
                    <label
                        className={'text-sm font-medium text-foreground'}
                        htmlFor={'whole-room-capacity'}
                    >
                        {t('services.divide_room.whole_capacity_label')}
                    </label>
                    <input
                        id={'whole-room-capacity'}
                        type={'number'}
                        min={1}
                        className={fieldClass}
                        value={wholeCapacity}
                        onChange={(e) => {
                            setWholeCapacityInput(e.target.value);
                            setFormError(null);
                        }}
                    />
                    <p className={'text-xs text-foreground/60'}>
                        {roomCapacity === null
                            ? t('services.divide_room.whole_capacity_hint_none')
                            : t('services.divide_room.whole_capacity_hint', { current: roomCapacity })}
                    </p>
                    {wholeProblem && (
                        <p className={'text-xs text-error'}>
                            {t(`services.divide_room.${wholeProblem}`)}
                        </p>
                    )}
                </div>

                {problem && (
                    <p className={'text-sm text-foreground/60'}>
                        {t(`services.divide_room.problem_${problem}`, {
                            min: MIN_SEGMENTS,
                            max: MAX_SEGMENTS,
                        })}
                    </p>
                )}
                {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
            </div>
        </CustomModal>
    );
};

export default DivideRoomDialog;
